import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { User, LogOut, Plus, BadgeCheck, Clock, Building2, Mail, Linkedin, FileText, Menu, X } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import { motion } from "framer-motion";

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  }
  return value.toLocaleString('en-IN');
};

// Verification method display
const getVerificationInfo = (method) => {
  switch (method) {
    case 'college_email':
      return { icon: Mail, label: 'via College Email', color: 'text-blue-600' };
    case 'linkedin':
      return { icon: Linkedin, label: 'via LinkedIn', color: 'text-sky-600' };
    case 'offer_letter':
      return { icon: FileText, label: 'via Offer Letter', color: 'text-green-600' };
    default:
      return null;
  }
};

const Profile = () => {
  const { user, logout, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchPlacements = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/my-placements`);
        setPlacements(response.data);
      } catch (error) {
        console.error("Error fetching placements:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchPlacements();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Not authenticated
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen">
        <nav className="border-b border-border bg-background sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight">
                Transplacement
              </Link>
              <Button onClick={() => login('/profile')} className="h-9 sm:h-10 px-4 sm:px-6 text-sm font-medium rounded-full">
                Sign In
              </Button>
            </div>
          </div>
        </nav>
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h1 className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight">
            Sign In Required
          </h1>
          <p className="text-muted-foreground mt-3 sm:mt-4 text-sm sm:text-base">
            Please sign in to view your profile.
          </p>
          <Button onClick={() => login('/profile')} className="mt-6 sm:mt-8 h-11 sm:h-12 px-6 sm:px-8 font-medium text-sm" data-testid="login-btn">
            Sign In with Google
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight" data-testid="logo">
              Transplacement
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/dashboard" className="text-sm font-medium hover:text-primary/80">
                Rankings
              </Link>
              <Link to="/submit" className="text-sm font-medium hover:text-primary/80">
                Submit
              </Link>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="h-10 px-4 text-sm font-medium rounded-full"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-background z-40 md:hidden">
          <div className="flex flex-col p-6 pt-20">
            <button 
              onClick={() => handleMobileNav('/')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Home
            </button>
            <button 
              onClick={() => handleMobileNav('/dashboard')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Rankings
            </button>
            <button 
              onClick={() => handleMobileNav('/submit')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Submit Placement
            </button>
            <button 
              onClick={handleLogout}
              className="mt-6 h-12 border border-border font-medium font-medium text-sm flex items-center justify-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile Header */}
        <div className="border border-border p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-12 h-12 sm:w-16 sm:h-16 border border-border object-cover"
              />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-secondary flex items-center justify-center border border-border">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-lg sm:text-2xl font-bold truncate" data-testid="user-name">{user?.name}</h1>
              <p className="text-muted-foreground text-sm sm:text-base truncate" data-testid="user-email">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <div className="card-data">
            <div className="font-mono text-xl sm:text-2xl font-bold" data-testid="submissions-count">
              {placements.length}
            </div>
            <div className="text-[10px] sm:text-xs text-muted-foreground font-medium mt-1">
              Submissions
            </div>
          </div>
          <div className="card-data">
            <div className="font-mono text-xl sm:text-2xl font-bold" data-testid="verified-count">
              {placements.filter(p => p.verification_status === 'verified').length}
            </div>
            <div className="text-[10px] sm:text-xs text-muted-foreground font-medium mt-1">
              Verified
            </div>
          </div>
          <div className="card-data">
            <div className="font-mono text-xl sm:text-2xl font-bold" data-testid="pending-count">
              {placements.filter(p => p.verification_status === 'pending').length}
            </div>
            <div className="text-[10px] sm:text-xs text-muted-foreground font-medium mt-1">
              Pending
            </div>
          </div>
        </div>

        {/* Submissions Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <h2 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-tight">
            Your Submissions
          </h2>
          <Button 
            onClick={() => navigate('/submit')}
            className="h-9 sm:h-10 px-4 sm:px-6 text-sm font-medium rounded-full w-full sm:w-auto justify-center"
            data-testid="new-submission-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Submission
          </Button>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="space-y-3 sm:space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-20 sm:h-24 w-full" />
            ))}
          </div>
        ) : placements.length === 0 ? (
          <div className="border border-border p-6 sm:p-8 text-center">
            <Building2 className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground mb-3 sm:mb-4" />
            <h3 className="font-heading text-base sm:text-lg font-bold">No Submissions Yet</h3>
            <p className="text-muted-foreground mt-2 mb-4 sm:mb-6 text-sm">
              Help make college placements transparent by submitting your placement details.
            </p>
            <Button 
              onClick={() => navigate('/submit')}
              className="h-11 sm:h-12 px-6 sm:px-8 font-medium text-sm w-full sm:w-auto"
              data-testid="first-submission-btn"
            >
              Submit Your First Placement
            </Button>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {placements.map((placement, index) => (
              <div 
                key={placement.placement_id} 
                className="border border-border p-3 sm:p-4 md:p-6 hover:border-primary/50 transition-colors"
                data-testid={`submission-${index}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
                      <h3 className="font-medium text-sm sm:text-base">{placement.company}</h3>
                      {placement.verification_status === 'verified' ? (
                        <span className="verification-badge verified">
                          <BadgeCheck className="h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="verification-badge pending">
                          <Clock className="h-3 w-3" />
                          Pending
                        </span>
                      )}
                      {placement.verification_status === 'verified' && placement.verification_method && (
                        (() => {
                          const info = getVerificationInfo(placement.verification_method);
                          if (!info) return null;
                          const Icon = info.icon;
                          return (
                            <span className={`text-[10px] sm:text-xs flex items-center gap-1 ${info.color}`}>
                              <Icon className="h-3 w-3" />
                              <span className="hidden sm:inline">{info.label}</span>
                            </span>
                          );
                        })()
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">
                      {placement.role} • {placement.college_name}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0">
                    <div className="font-mono text-lg sm:text-xl font-bold">
                      {formatCurrency(placement.total_ctc)}
                    </div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground">
                      {placement.year}
                    </div>
                  </div>
                </div>

                {/* Salary Breakdown - Hide some details on mobile */}
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs sm:text-sm">
                  <div>
                    <span className="text-muted-foreground">Fixed:</span>
                    <span className="font-mono ml-1 sm:ml-2">{formatCurrency(placement.salary_breakdown.fixed)}</span>
                  </div>
                  {placement.salary_breakdown.variable > 0 && (
                    <div>
                      <span className="text-muted-foreground">Variable:</span>
                      <span className="font-mono ml-1 sm:ml-2">{formatCurrency(placement.salary_breakdown.variable)}</span>
                    </div>
                  )}
                  {placement.salary_breakdown.esops > 0 && (
                    <div className="hidden sm:block">
                      <span className="text-muted-foreground">ESOPs:</span>
                      <span className="font-mono ml-1 sm:ml-2">{formatCurrency(placement.salary_breakdown.esops)}</span>
                    </div>
                  )}
                  {placement.salary_breakdown.joining_bonus > 0 && (
                    <div className="hidden md:block">
                      <span className="text-muted-foreground">Bonus:</span>
                      <span className="font-mono ml-1 sm:ml-2">{formatCurrency(placement.salary_breakdown.joining_bonus)}</span>
                    </div>
                  )}
                  {placement.salary_breakdown.relocation > 0 && (
                    <div className="hidden md:block">
                      <span className="text-muted-foreground">Relocation:</span>
                      <span className="font-mono ml-1 sm:ml-2">{formatCurrency(placement.salary_breakdown.relocation)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
